/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * SuperAdmin Role Templates Page — 3-column Access Grant layout.
 *
 * Left rail: template cards with search + system/custom grouping.
 * Center: collapsible module blocks with permission checkboxes (pgrp layout).
 * Right panel: broadcast targets + audit trail.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Plus, ChevronDown, Save, Trash2,
  Users, Package, MapPin, ShoppingCart, Truck, Boxes, Banknote,
  BarChart3, Settings, X, Check, Loader2, Radio, Clock, Shield,
  Tags, ClipboardEdit, AlertTriangle, ArrowDownCircle,
  Grid3X3, ArrowLeftRight, CheckSquare, ClipboardList,
  MonitorPlay, FileText, Receipt,
  PackageCheck, FileWarning, CreditCard,
  Landmark, Smartphone, RefreshCw, History, DollarSign,
  ArrowUpCircle, TrendingUp, BarChart2, PieChart,
  PackageSearch, Repeat2, Calculator, Percent,
  UsersRound, Hash, Ruler, FileImage, Monitor,
  LayoutDashboard, Building2, CreditCard as CreditCardIcon,
  Users2, BookOpen, Wrench, Activity, Lock, Flag,
  Puzzle, Globe, FileCheck, DatabaseBackup, Info,
  BookOpenCheck,
} from 'lucide-react';
import { useNotification } from '@/hooks/useNotification';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import {
  useRoleTemplates, useTemplatePermissions, useBroadcastTargets,
  useCreateTemplate, useUpdateTemplate, useDeleteTemplate,
  useBroadcastTemplate,
} from '@/hooks/api/useRoleTemplates';
import type {
  RoleTemplateDto, PermissionDto, CreateRoleTemplateRequest,
} from '@/services/superadmin/role-templates.service';

// ─── Module Metadata ────────────────────────────────────────────────────────

interface ModuleMeta {
  key: string;
  icon: React.ElementType;
  emoji: string;
  colorVar: string;
}

const MODULE_META: ModuleMeta[] = [
  // ── Inventory ──
  { key: 'PRODUCTS',         icon: Package,         emoji: '📦', colorVar: 'var(--color-success)' },
  { key: 'CATEGORIES',       icon: Tags,            emoji: '🏷️', colorVar: 'var(--color-success)' },
  { key: 'STOCK',            icon: Boxes,           emoji: '📋', colorVar: 'var(--color-success)' },
  { key: 'STOCK_ADJUST',     icon: ClipboardEdit,   emoji: '✏️', colorVar: 'var(--color-success)' },
  { key: 'STOCK_ALERTS',     icon: AlertTriangle,   emoji: '⚠️', colorVar: 'var(--color-success)' },
  { key: 'PRODUCT_IMPORT',   icon: ArrowDownCircle, emoji: '⬇️', colorVar: 'var(--color-success)' },
  // ── Warehouse ──
  { key: 'LOCATIONS',        icon: MapPin,          emoji: '📍', colorVar: 'var(--color-success)' },
  { key: 'STOCK_MATRIX',     icon: Grid3X3,         emoji: '🔢', colorVar: 'var(--color-success)' },
  { key: 'TRANSFERS',        icon: ArrowLeftRight,  emoji: '🔀', colorVar: 'var(--color-success)' },
  { key: 'TRANSFER_APPROVALS', icon: CheckSquare,   emoji: '✅', colorVar: 'var(--color-success)' },
  { key: 'INVENTORY_COUNTS', icon: ClipboardList,   emoji: '📝', colorVar: 'var(--color-success)' },
  // ── Partners ──
  { key: 'PARTNERS',         icon: Users,           emoji: '🤝', colorVar: 'var(--color-brand-primary)' },
  // ── Sales ──
  { key: 'POS',              icon: MonitorPlay,     emoji: '🖥️', colorVar: 'var(--color-brand-primary)' },
  { key: 'QUOTATIONS',       icon: FileText,        emoji: '📄', colorVar: 'var(--color-brand-primary)' },
  { key: 'SALES_ORDERS',     icon: ShoppingCart,    emoji: '🛒', colorVar: 'var(--color-brand-primary)' },
  { key: 'INVOICES',         icon: Receipt,         emoji: '🧾', colorVar: 'var(--color-brand-primary)' },
  // ── Purchasing ──
  { key: 'PURCHASE_ORDERS',  icon: Truck,           emoji: '🚚', colorVar: 'var(--color-info)' },
  { key: 'GOODS_RECEIPTS',   icon: PackageCheck,    emoji: '📥', colorVar: 'var(--color-info)' },
  { key: 'PURCHASE_RETURNS', icon: FileWarning,     emoji: '↩️', colorVar: 'var(--color-info)' },
  { key: 'DEBIT_NOTES',      icon: CreditCard,      emoji: '💳', colorVar: 'var(--color-info)' },
  // ── Cash & Bank ──
  { key: 'CASH_REGISTERS',   icon: Banknote,        emoji: '💵', colorVar: 'var(--color-warning)' },
  { key: 'BANK_ACCOUNTS',    icon: Landmark,        emoji: '🏦', colorVar: 'var(--color-warning)' },
  { key: 'MOBILE_WALLETS',   icon: Smartphone,      emoji: '📱', colorVar: 'var(--color-warning)' },
  { key: 'RECONCILIATION',   icon: RefreshCw,       emoji: '🔄', colorVar: 'var(--color-warning)' },
  { key: 'RECON_HISTORY',    icon: History,         emoji: '🕓', colorVar: 'var(--color-warning)' },
  { key: 'CASH_FLOW',        icon: DollarSign,      emoji: '💲', colorVar: 'var(--color-warning)' },
  // ── Transactions ──
  { key: 'CASH_RECEIPTS',    icon: ArrowDownCircle, emoji: '⬇️', colorVar: 'var(--color-warning)' },
  { key: 'PAYMENT_VOUCHERS', icon: ArrowUpCircle,   emoji: '⬆️', colorVar: 'var(--color-warning)' },
  { key: 'BANK_TRANSFERS',   icon: ArrowLeftRight,  emoji: '↔️', colorVar: 'var(--color-warning)' },
  // ── Analytics ──
  { key: 'SALES_DASHBOARD',  icon: TrendingUp,      emoji: '📈', colorVar: 'var(--color-info)' },
  { key: 'SALES_REPORTS',    icon: BarChart3,        emoji: '📊', colorVar: 'var(--color-info)' },
  { key: 'BUSINESS_PULSE',   icon: BarChart2,        emoji: '💓', colorVar: 'var(--color-info)' },
  // ── Reports ──
  { key: 'RPT_SALES',          icon: PieChart,      emoji: '🥧', colorVar: 'var(--color-info)' },
  { key: 'RPT_SALES_PRODUCT',  icon: PieChart,      emoji: '🥧', colorVar: 'var(--color-info)' },
  { key: 'RPT_SALES_CUSTOMER', icon: PieChart,      emoji: '🥧', colorVar: 'var(--color-info)' },
  { key: 'RPT_INV_VALUATION',  icon: PackageSearch, emoji: '🔍', colorVar: 'var(--color-info)' },
  { key: 'RPT_INV_MOVEMENT',   icon: Repeat2,       emoji: '🔁', colorVar: 'var(--color-info)' },
  { key: 'RPT_PURCHASES',      icon: PieChart,      emoji: '🥧', colorVar: 'var(--color-info)' },
  { key: 'RPT_CASH_FLOW',      icon: Calculator,    emoji: '🧮', colorVar: 'var(--color-info)' },
  { key: 'RPT_AGING',          icon: PieChart,      emoji: '🥧', colorVar: 'var(--color-info)' },
  { key: 'RPT_TAX',            icon: Percent,       emoji: '💹', colorVar: 'var(--color-info)' },
  // ── Administration ──
  { key: 'USERS',            icon: UsersRound,      emoji: '👥', colorVar: 'var(--color-error)' },
  { key: 'ROLES',            icon: Shield,          emoji: '🛡️', colorVar: 'var(--color-error)' },
  // ── Configuration ──
  { key: 'TAX_CONFIG',       icon: Settings,        emoji: '⚙️', colorVar: 'var(--color-text-muted)' },
  { key: 'DOC_NUMBERING',    icon: Hash,            emoji: '#️⃣', colorVar: 'var(--color-text-muted)' },
  { key: 'UNITS',            icon: Ruler,           emoji: '📏', colorVar: 'var(--color-text-muted)' },
  { key: 'FX_RATES',         icon: DollarSign,      emoji: '💱', colorVar: 'var(--color-text-muted)' },
  { key: 'INVOICE_TEMPLATES', icon: FileImage,      emoji: '🖼️', colorVar: 'var(--color-text-muted)' },
  { key: 'SETTINGS',         icon: Settings,        emoji: '⚙️', colorVar: 'var(--color-text-muted)' },
  { key: 'POS_SETTINGS',     icon: Monitor,         emoji: '🖥️', colorVar: 'var(--color-text-muted)' },
];

function getModuleMeta(key: string): ModuleMeta {
  return MODULE_META.find(m => m.key === key) || { key, icon: Package, emoji: '📁', colorVar: 'var(--color-text-muted)' };
}

// ─── Section Groups (matches VENTA sidebar structure) ────────────────────────

interface SectionDef {
  key: string;
  labelKey: string;
  modules: string[];
  icon: React.ElementType;
  colorVar: string;
}

const SECTION_GROUPS: SectionDef[] = [
  {
    key: 'SEC_INVENTORY',
    labelKey: 'roleTemplates.sections.inventory',
    modules: ['PRODUCTS', 'CATEGORIES', 'STOCK', 'STOCK_ADJUST', 'STOCK_ALERTS', 'PRODUCT_IMPORT'],
    icon: Package,
    colorVar: 'var(--color-success)',
  },
  {
    key: 'SEC_WAREHOUSE',
    labelKey: 'roleTemplates.sections.warehouse',
    modules: ['LOCATIONS', 'STOCK_MATRIX', 'TRANSFERS', 'TRANSFER_APPROVALS', 'INVENTORY_COUNTS'],
    icon: MapPin,
    colorVar: 'var(--color-success)',
  },
  {
    key: 'SEC_PARTNERS',
    labelKey: 'roleTemplates.sections.partners',
    modules: ['PARTNERS'],
    icon: Users,
    colorVar: 'var(--color-brand-primary)',
  },
  {
    key: 'SEC_SALES',
    labelKey: 'roleTemplates.sections.sales',
    modules: ['POS', 'QUOTATIONS', 'SALES_ORDERS', 'INVOICES'],
    icon: ShoppingCart,
    colorVar: 'var(--color-brand-primary)',
  },
  {
    key: 'SEC_PURCHASING',
    labelKey: 'roleTemplates.sections.purchasing',
    modules: ['PURCHASE_ORDERS', 'GOODS_RECEIPTS', 'PURCHASE_RETURNS', 'DEBIT_NOTES', 'COST_SHEETS'],
    icon: Truck,
    colorVar: 'var(--color-info)',
  },
  {
    key: 'SEC_CASHBANK',
    labelKey: 'roleTemplates.sections.cashBank',
    modules: ['CASH_REGISTERS', 'BANK_ACCOUNTS', 'MOBILE_WALLETS', 'RECONCILIATION', 'RECON_HISTORY', 'CASH_FLOW'],
    icon: Banknote,
    colorVar: 'var(--color-warning)',
  },
  {
    key: 'SEC_TRANSACTIONS',
    labelKey: 'roleTemplates.sections.transactions',
    modules: ['CASH_RECEIPTS', 'PAYMENT_VOUCHERS', 'BANK_TRANSFERS'],
    icon: ArrowLeftRight,
    colorVar: 'var(--color-warning)',
  },
  {
    key: 'SEC_ANALYTICS',
    labelKey: 'roleTemplates.sections.analytics',
    modules: ['SALES_DASHBOARD', 'SALES_REPORTS', 'BUSINESS_PULSE'],
    icon: TrendingUp,
    colorVar: 'var(--color-info)',
  },
  {
    key: 'SEC_REPORTS',
    labelKey: 'roleTemplates.sections.reports',
    modules: ['RPT_SALES', 'RPT_SALES_PRODUCT', 'RPT_SALES_CUSTOMER', 'RPT_INV_VALUATION', 'RPT_INV_MOVEMENT', 'RPT_PURCHASES', 'RPT_CASH_FLOW', 'RPT_AGING', 'RPT_TAX'],
    icon: BarChart3,
    colorVar: 'var(--color-info)',
  },
  {
    key: 'SEC_ADMIN',
    labelKey: 'roleTemplates.sections.administration',
    modules: ['USERS', 'ROLES'],
    icon: UsersRound,
    colorVar: 'var(--color-error)',
  },
  {
    key: 'SEC_CONFIG',
    labelKey: 'roleTemplates.sections.configuration',
    modules: ['TAX_CONFIG', 'DOC_NUMBERING', 'UNITS', 'FX_RATES', 'INVOICE_TEMPLATES', 'SETTINGS', 'POS_SETTINGS'],
    icon: Settings,
    colorVar: 'var(--color-text-muted)',
  },
];

// ─── Module Tabs ─────────────────────────────────────────────────────────────

type ModuleTab = 'VENTA' | 'SUPERADMIN' | 'COMPTA';

const MODULE_TABS: { key: ModuleTab; labelKey: string; icon: React.ElementType; color: string }[] = [
  { key: 'VENTA',      labelKey: 'roleTemplates.moduleTabs.venta',      icon: ShoppingCart,    color: 'var(--color-brand-primary)' },
  { key: 'SUPERADMIN', labelKey: 'roleTemplates.moduleTabs.superadmin', icon: Shield,          color: 'var(--color-error)' },
  { key: 'COMPTA',     labelKey: 'roleTemplates.moduleTabs.compta',     icon: BookOpenCheck,   color: 'var(--color-info)' },
];

// ─── SuperAdmin Sections (read-only, mirrors sidebar) ────────────────────────

interface SuperAdminMenuItem { key: string; icon: React.ElementType; labelKey: string }
interface SuperAdminSection { key: string; labelKey: string; icon: React.ElementType; colorVar: string; items: SuperAdminMenuItem[] }

const SUPERADMIN_SECTIONS: SuperAdminSection[] = [
  {
    key: 'SA_OVERVIEW', labelKey: 'roleTemplates.saSections.overview', icon: LayoutDashboard, colorVar: 'var(--color-brand-primary)',
    items: [
      { key: 'SA_DASHBOARD', icon: LayoutDashboard, labelKey: 'roleTemplates.saItems.dashboard' },
    ],
  },
  {
    key: 'SA_ORGANIZATIONS', labelKey: 'roleTemplates.saSections.organizations', icon: Building2, colorVar: 'var(--color-success)',
    items: [
      { key: 'SA_ALL_ORGS',      icon: Building2,      labelKey: 'roleTemplates.saItems.allOrganizations' },
      { key: 'SA_REGISTRATIONS',  icon: ClipboardList,  labelKey: 'roleTemplates.saItems.registrations' },
    ],
  },
  {
    key: 'SA_BILLING', labelKey: 'roleTemplates.saSections.billing', icon: CreditCardIcon, colorVar: 'var(--color-warning)',
    items: [
      { key: 'SA_MODULE_PRICING',  icon: DollarSign,    labelKey: 'roleTemplates.saItems.modulePricing' },
      { key: 'SA_INVOICES',        icon: Receipt,       labelKey: 'roleTemplates.saItems.invoices' },
      { key: 'SA_PAYMENTS',        icon: Banknote,      labelKey: 'roleTemplates.saItems.allPayments' },
      { key: 'SA_SUBSCRIPTIONS',   icon: RefreshCw,     labelKey: 'roleTemplates.saItems.subscriptions' },
      { key: 'SA_ADDONS',          icon: Puzzle,        labelKey: 'roleTemplates.saItems.featureAddons' },
    ],
  },
  {
    key: 'SA_PARTNERS', labelKey: 'roleTemplates.saSections.partners', icon: Users2, colorVar: 'var(--color-info)',
    items: [
      { key: 'SA_PARTNERS_LIST',  icon: Users2,      labelKey: 'roleTemplates.saItems.partners' },
      { key: 'SA_COMMISSIONS',    icon: Percent,         labelKey: 'roleTemplates.saItems.commissions' },
      { key: 'SA_WITHDRAWALS',    icon: ArrowUpCircle,   labelKey: 'roleTemplates.saItems.withdrawals' },
      { key: 'SA_INVITE_CODES',   icon: FileText,        labelKey: 'roleTemplates.saItems.inviteCodes' },
    ],
  },
  {
    key: 'SA_LEDGER', labelKey: 'roleTemplates.saSections.ledgerAudit', icon: BookOpen, colorVar: 'var(--color-brand-primary)',
    items: [
      { key: 'SA_JOURNAL',        icon: BookOpen,        labelKey: 'roleTemplates.saItems.journalExplorer' },
      { key: 'SA_TRIAL_BALANCE',  icon: Calculator,      labelKey: 'roleTemplates.saItems.trialBalance' },
      { key: 'SA_LEDGER_ACCTS',   icon: Landmark,        labelKey: 'roleTemplates.saItems.ledgerAccounts' },
      { key: 'SA_BALANCE_VERIFY', icon: CheckSquare,     labelKey: 'roleTemplates.saItems.balanceVerification' },
    ],
  },
  {
    key: 'SA_SYSTEM', labelKey: 'roleTemplates.saSections.system', icon: Wrench, colorVar: 'var(--color-text-muted)',
    items: [
      { key: 'SA_ROLE_TEMPLATES',   icon: Shield,          labelKey: 'roleTemplates.saItems.roleTemplates' },
      { key: 'SA_DATA_INTEGRITY',   icon: Activity,        labelKey: 'roleTemplates.saItems.dataIntegrity' },
      { key: 'SA_SYSTEM_HEALTH',    icon: Activity,        labelKey: 'roleTemplates.saItems.systemHealth' },
      { key: 'SA_JOBS',             icon: Clock,           labelKey: 'roleTemplates.saItems.scheduledJobs' },
      { key: 'SA_SUPPORT',          icon: Wrench,          labelKey: 'roleTemplates.saItems.supportTools' },
      { key: 'SA_AUDIT_LOGS',       icon: FileText,        labelKey: 'roleTemplates.saItems.auditLogs' },
      { key: 'SA_WEBHOOKS',         icon: Globe,           labelKey: 'roleTemplates.saItems.webhooks' },
      { key: 'SA_EVENTS',           icon: Radio,           labelKey: 'roleTemplates.saItems.eventDashboard' },
      { key: 'SA_EMAILS',           icon: FileText,        labelKey: 'roleTemplates.saItems.emailDashboard' },
      { key: 'SA_SECURITY',         icon: Lock,            labelKey: 'roleTemplates.saItems.security' },
      { key: 'SA_FEATURE_FLAGS',    icon: Flag,            labelKey: 'roleTemplates.saItems.featureFlags' },
      { key: 'SA_MODULES',          icon: Puzzle,          labelKey: 'roleTemplates.saItems.moduleManagement' },
      { key: 'SA_SETTINGS',         icon: Settings,        labelKey: 'roleTemplates.saItems.platformSettings' },
      { key: 'SA_CURRENCIES',       icon: DollarSign,      labelKey: 'roleTemplates.saItems.currencies' },
      { key: 'SA_INV_SETTINGS',     icon: FileCheck,       labelKey: 'roleTemplates.saItems.invoiceSettings' },
      { key: 'SA_TEMPLATES',        icon: FileImage,       labelKey: 'roleTemplates.saItems.templateCatalog' },
      { key: 'SA_MIGRATION',        icon: DatabaseBackup,  labelKey: 'roleTemplates.saItems.dataMigration' },
    ],
  },
];

function getTemplateInitials(code: string): string {
  return code.slice(0, 2).toUpperCase();
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function RoleTemplatesPage() {
  const { t } = useTranslation('platform');
  const notification = useNotification();

  // UI state (moduleTab declared before API hooks so it can be used as query param)
  const [moduleTab, setModuleTab] = useState<ModuleTab>('VENTA');

  // API hooks
  const { data: templates = [], isLoading: templatesLoading } = useRoleTemplates(moduleTab);
  const { data: allPermissions = [], isLoading: permsLoading } = useTemplatePermissions();
  const { data: broadcastTargets = [] } = useBroadcastTargets();
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();
  const broadcastMutation = useBroadcastTemplate();

  // UI state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [roleSearch, setRoleSearch] = useState('');
  const [permSearch, setPermSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedSaSections, setExpandedSaSections] = useState<Set<string>>(new Set());
  const [pendingPerms, setPendingPerms] = useState<Set<string> | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ code: '', name: '', description: '', hierarchyLevel: 0 });
  const [showBroadcastConfirm, setShowBroadcastConfirm] = useState(false);

  // Reset selection when module tab changes
  useEffect(() => { setSelectedId(null); }, [moduleTab]);

  // Select first template when loaded
  useEffect(() => {
    if (templates.length > 0 && !selectedId) {
      setSelectedId(templates[0].id);
    }
  }, [templates, selectedId]);

  // Reset pending when selection changes
  useEffect(() => { setPendingPerms(null); }, [selectedId]);

  // Derived
  const selected = useMemo(
    () => templates.find(tpl => tpl.id === selectedId) || null,
    [templates, selectedId],
  );

  const systemTemplates = useMemo(() => templates.filter(tpl => tpl.system), [templates]);
  const customTemplates = useMemo(() => templates.filter(tpl => !tpl.system), [templates]);

  const filteredSystem = useMemo(() => {
    if (!roleSearch) return systemTemplates;
    const q = roleSearch.toLowerCase();
    return systemTemplates.filter(tpl => tpl.name.toLowerCase().includes(q) || tpl.code.toLowerCase().includes(q));
  }, [systemTemplates, roleSearch]);

  const filteredCustom = useMemo(() => {
    if (!roleSearch) return customTemplates;
    const q = roleSearch.toLowerCase();
    return customTemplates.filter(tpl => tpl.name.toLowerCase().includes(q) || tpl.code.toLowerCase().includes(q));
  }, [customTemplates, roleSearch]);

  const permsByModule = useMemo(() => {
    const map: Record<string, PermissionDto[]> = {};
    for (const p of allPermissions) {
      if (!map[p.module]) map[p.module] = [];
      map[p.module].push(p);
    }
    return map;
  }, [allPermissions]);

  const permIdLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allPermissions) map.set(`${p.module}:${p.action}`, p.id);
    return map;
  }, [allPermissions]);

  const currentPermIds = useMemo(() => {
    if (pendingPerms) return pendingPerms;
    if (!selected) return new Set<string>();
    const ids = new Set<string>();
    for (const [mod, actions] of Object.entries(selected.permissions)) {
      for (const action of actions) {
        const id = permIdLookup.get(`${mod}:${action}`);
        if (id) ids.add(id);
      }
    }
    return ids;
  }, [pendingPerms, selected, permIdLookup]);

  const hasUnsaved = pendingPerms !== null;

  const filteredSections = useMemo(() => {
    if (!permSearch) return SECTION_GROUPS;
    const q = permSearch.toLowerCase();
    return SECTION_GROUPS.map(section => {
      const matchedModules = section.modules.filter(mod => {
        const perms = permsByModule[mod] || [];
        if (perms.length === 0) return false;
        const moduleLabel = t(`roleTemplates.modules.${mod}`, mod).toLowerCase();
        if (moduleLabel.includes(q)) return true;
        return perms.some(p => {
          const actionLabel = t(`roleTemplates.actions.${p.action}`, p.action).toLowerCase();
          return actionLabel.includes(q) || p.description.toLowerCase().includes(q);
        });
      });
      if (matchedModules.length === 0) {
        const sectionLabel = t(section.labelKey, '').toLowerCase();
        if (sectionLabel.includes(q)) return section;
        return null;
      }
      return { ...section, modules: matchedModules };
    }).filter((s): s is SectionDef => s !== null);
  }, [permsByModule, permSearch, t]);

  const totalPerms = allPermissions.length;
  const grantedPerms = currentPermIds.size;

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setPermSearch('');
  }, []);

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey); else next.add(sectionKey);
      return next;
    });
  }, []);

  const toggleSaSection = useCallback((sectionKey: string) => {
    setExpandedSaSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey); else next.add(sectionKey);
      return next;
    });
  }, []);

  const toggleSectionAll = useCallback((section: SectionDef) => {
    const sectionPerms = section.modules.flatMap(mod => permsByModule[mod] || []);
    const sectionPermIds = sectionPerms.map(p => p.id);
    const allChecked = sectionPermIds.every(id => (pendingPerms || currentPermIds).has(id));

    setPendingPerms(prev => {
      const base = new Set(prev || currentPermIds);
      if (allChecked) {
        sectionPermIds.forEach(id => base.delete(id));
      } else {
        sectionPermIds.forEach(id => base.add(id));
      }
      return base;
    });
  }, [permsByModule, pendingPerms, currentPermIds]);

  const toggleModuleAll = useCallback((modKey: string) => {
    const perms = permsByModule[modKey] || [];
    const permIds = perms.map(p => p.id);
    const allChecked = permIds.every(id => (pendingPerms || currentPermIds).has(id));

    setPendingPerms(prev => {
      const base = new Set(prev || currentPermIds);
      if (allChecked) {
        permIds.forEach(id => base.delete(id));
      } else {
        permIds.forEach(id => base.add(id));
      }
      return base;
    });
  }, [permsByModule, pendingPerms, currentPermIds]);

  const togglePerm = useCallback((permId: string) => {
    setPendingPerms(prev => {
      const base = prev || new Set(currentPermIds);
      const next = new Set(base);
      if (next.has(permId)) next.delete(permId); else next.add(permId);
      return next;
    });
  }, [currentPermIds]);

  const selectAll = useCallback(() => {
    setPendingPerms(new Set(allPermissions.map(p => p.id)));
  }, [allPermissions]);

  const deselectAll = useCallback(() => {
    setPendingPerms(new Set());
  }, []);

  const handleSave = useCallback(async () => {
    if (!selected || !pendingPerms) return;
    try {
      await updateMutation.mutateAsync({
        id: selected.id,
        data: { permissionIds: Array.from(pendingPerms) },
      });
      setPendingPerms(null);
      notification.success(t('roleTemplates.messages.saved'));
    } catch (err) {
      const { message } = isApiError(err)
        ? formatErrorForDisplay(err, t)
        : { message: t('roleTemplates.messages.saveFailed') };
      notification.error(message);
    }
  }, [selected, pendingPerms, updateMutation, notification, t]);

  const handleCreate = useCallback(async () => {
    if (!newTemplate.code || !newTemplate.name) return;
    try {
      const req: CreateRoleTemplateRequest = {
        code: newTemplate.code,
        name: newTemplate.name,
        description: newTemplate.description || undefined,
        hierarchyLevel: newTemplate.hierarchyLevel,
        moduleScope: moduleTab,
      };
      const created = await createMutation.mutateAsync(req);
      setShowNewModal(false);
      setNewTemplate({ code: '', name: '', description: '', hierarchyLevel: 0 });
      setSelectedId(created.id);
      notification.success(t('roleTemplates.messages.created'));
    } catch (err) {
      const { message } = isApiError(err)
        ? formatErrorForDisplay(err, t)
        : { message: t('roleTemplates.messages.createFailed') };
      notification.error(message);
    }
  }, [newTemplate, moduleTab, createMutation, notification, t]);

  const handleDelete = useCallback(async () => {
    if (!selected || selected.system) return;
    try {
      await deleteMutation.mutateAsync(selected.id);
      setSelectedId(null);
      notification.success(t('roleTemplates.messages.deleted'));
    } catch (err) {
      const { message } = isApiError(err)
        ? formatErrorForDisplay(err, t)
        : { message: t('roleTemplates.messages.deleteFailed') };
      notification.error(message);
    }
  }, [selected, deleteMutation, notification, t]);

  const handleBroadcast = useCallback(async () => {
    if (!selected) return;
    setShowBroadcastConfirm(false);
    try {
      const result = await broadcastMutation.mutateAsync(selected.id);
      notification.success(
        t('roleTemplates.messages.broadcastSuccess', {
          created: result.createdOrgs,
          updated: result.updatedOrgs,
          total: result.totalOrgs,
        })
      );
    } catch (err) {
      const { message } = isApiError(err)
        ? formatErrorForDisplay(err, t)
        : { message: t('roleTemplates.messages.broadcastFailed') };
      notification.error(message);
    }
  }, [selected, broadcastMutation, notification, t]);

  // ─── Loading ────────────────────────────────────────────────────────────

  if (templatesLoading || permsLoading) {
    return (
      <div className="sart-workspace" style={{ height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8, gridColumn: '1 / -1' }}>
          <Loader2 className="sart-spin" style={{ width: 20, height: 20, color: 'var(--color-text-muted)' }} />
          <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('roleTemplates.loading')}</span>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <div className="sart-workspace">

        {/* ═══ LEFT RAIL ═══ */}
        <div className="sart-left">
          <div className="sart-rail-head">
            <div className="sart-rail-head-row">
              <span className="sart-rail-label">{t('roleTemplates.globalTemplates')}</span>
            </div>
            <div className="sart-search-wrap">
              <Search style={{ width: 12, height: 12 }} />
              <input
                type="text"
                placeholder={t('roleTemplates.searchTemplates')}
                value={roleSearch}
                onChange={e => setRoleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="sart-role-scroll">
            {/* Global Templates */}
            <div className="sart-grp-label">{t('roleTemplates.globalTemplatesLabel')}</div>
            {filteredSystem.map(tpl => (
              <RoleCard
                key={tpl.id}
                template={tpl}
                active={tpl.id === selectedId}
                onClick={() => handleSelect(tpl.id)}
              />
            ))}
            {filteredCustom.map(tpl => (
              <RoleCard
                key={tpl.id}
                template={tpl}
                active={tpl.id === selectedId}
                onClick={() => handleSelect(tpl.id)}
              />
            ))}
          </div>

          {moduleTab !== 'COMPTA' && (
            <button className="sart-new-role-btn" onClick={() => setShowNewModal(true)}>
              <Plus style={{ width: 13, height: 13 }} />
              {t('roleTemplates.createNew')}
            </button>
          )}
        </div>

        {/* ═══ CENTER PANEL ═══ */}
        <div className="sart-center">
          {/* Module Tab Bar — always visible */}
          <div className="sart-module-tabs">
            {MODULE_TABS.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  className={`sart-module-tab ${moduleTab === tab.key ? 'active' : ''}`}
                  onClick={() => setModuleTab(tab.key)}
                  style={{ '--tab-color': tab.color } as React.CSSProperties}
                >
                  <TabIcon style={{ width: 13, height: 13 }} />
                  {t(tab.labelKey, tab.key)}
                </button>
              );
            })}
          </div>

          {selected ? (
            <>
              {/* Role Header */}
              <div className="sart-role-hdr">
                <div className="sart-role-hdr-icon">
                  {getTemplateInitials(selected.code)}
                </div>
                <div className="sart-role-hdr-info">
                  <div className="sart-role-hdr-name">{selected.name}</div>
                  <div className="sart-role-hdr-meta">
                    <div className="sart-meta-item">
                      <Users style={{ width: 11, height: 11 }} />
                      <strong>{t('roleTemplates.hierarchy', { level: selected.hierarchyLevel })}</strong>
                    </div>
                    <div className="sart-meta-dot" />
                    <div className="sart-meta-item">
                      <Radio style={{ width: 11, height: 11 }} />
                      <strong>{t('roleTemplates.broadcastScope', { count: broadcastTargets.length })}</strong>
                    </div>
                    <div className="sart-meta-dot" />
                    <div className="sart-audit-chip">
                      <Clock style={{ width: 10, height: 10 }} />
                      {selected.updatedAt
                        ? new Date(selected.updatedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
                        : new Date(selected.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
                      }
                    </div>
                  </div>
                </div>
                <div className="sart-hdr-actions">
                  {!selected.system && (
                    <button className="sart-btn sart-btn-ghost" onClick={handleDelete} title="Delete">
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  )}
                  <button
                    className="sart-btn sart-btn-warn"
                    onClick={() => setShowBroadcastConfirm(true)}
                    disabled={broadcastMutation.isPending}
                  >
                    <Radio style={{ width: 12, height: 12 }} />
                    {t('roleTemplates.broadcast')}
                  </button>
                  <button
                    className="sart-btn sart-btn-primary"
                    onClick={handleSave}
                    disabled={!hasUnsaved || updateMutation.isPending}
                  >
                    <Save style={{ width: 12, height: 12 }} />
                    {updateMutation.isPending ? t('roleTemplates.saving') : t('roleTemplates.saveChanges')}
                  </button>
                </div>
              </div>

              {/* ─── VENTA Tab ─── */}
              {moduleTab === 'VENTA' && (
                <>
                  {/* Toolbar */}
                  <div className="sart-toolbar">
                    <div className="sart-tb-search">
                      <Search style={{ width: 11, height: 11 }} />
                      <input
                        type="text"
                        placeholder={t('roleTemplates.filterPerms')}
                        value={permSearch}
                        onChange={e => setPermSearch(e.target.value)}
                      />
                    </div>
                    <div className="sart-tb-sep" />
                    <button className="sart-tb-btn" onClick={selectAll}>{t('roleTemplates.selectAll')}</button>
                    <button className="sart-tb-btn" onClick={deselectAll}>{t('roleTemplates.deselectAll')}</button>
                    <div className="sart-tb-right">
                      <div className="sart-counter">
                        <b>{grantedPerms}</b> / <span>{totalPerms}</span> {t('roleTemplates.granted')}
                      </div>
                    </div>
                  </div>

                  {/* Permission Sections */}
                  <div className="sart-perms-scroll">
                    {filteredSections.map(section => {
                      const sectionPerms = section.modules.flatMap(mod => permsByModule[mod] || []);
                      if (sectionPerms.length === 0) return null;
                      const sectionChecked = sectionPerms.filter(p => currentPermIds.has(p.id)).length;
                      const isOpen = expandedSections.has(section.key);
                      const allChecked = sectionChecked === sectionPerms.length && sectionPerms.length > 0;
                      const SectionIcon = section.icon;

                      return (
                        <div key={section.key} className={`sart-section ${isOpen ? 'open' : ''}`}>
                          {/* Section Header */}
                          <div className="sart-section-hdr" onClick={() => toggleSection(section.key)}>
                            <div className="sart-section-icon" style={{ background: `color-mix(in srgb, ${section.colorVar} 15%, transparent)`, color: section.colorVar }}>
                              <SectionIcon style={{ width: 14, height: 14 }} />
                            </div>
                            <div className="sart-section-label">{t(section.labelKey)}</div>
                            {/* Master toggle for entire section */}
                            <div
                              className={`sart-section-toggle ${allChecked ? 'on' : sectionChecked > 0 ? 'partial' : ''}`}
                              onClick={e => { e.stopPropagation(); toggleSectionAll(section); }}
                            >
                              {allChecked && <Check style={{ width: 10, height: 10 }} />}
                            </div>
                            <div className="sart-section-count">{sectionChecked} / {sectionPerms.length}</div>
                            <ChevronDown className="sart-chev" style={{ width: 14, height: 14 }} />
                          </div>

                          {/* Section Body — Module Items */}
                          {isOpen && (
                            <div className="sart-section-body">
                              {section.modules.map(modKey => {
                                const perms = permsByModule[modKey] || [];
                                if (perms.length === 0) return null;
                                const meta = getModuleMeta(modKey);
                                const ModIcon = meta.icon;
                                const modChecked = perms.filter(p => currentPermIds.has(p.id)).length;
                                const allModChecked = modChecked === perms.length;

                                return (
                                  <div key={modKey} className="sart-menu-item">
                                    {/* Module toggle */}
                                    <div
                                      className={`sart-item-toggle ${allModChecked ? 'on' : modChecked > 0 ? 'partial' : ''}`}
                                      onClick={() => toggleModuleAll(modKey)}
                                    />
                                    <div className="sart-item-icon" style={{ color: section.colorVar }}>
                                      <ModIcon style={{ width: 13, height: 13 }} />
                                    </div>
                                    <div className="sart-item-label">{t(`roleTemplates.modules.${modKey}`, modKey)}</div>
                                    {/* Action pills */}
                                    <div className="sart-item-pills">
                                      {perms.map(perm => {
                                        const isOn = currentPermIds.has(perm.id);
                                        const letter = perm.action.charAt(0);
                                        return (
                                          <div
                                            key={perm.id}
                                            className={`sart-pill ${isOn ? 'on' : ''}`}
                                            onClick={() => togglePerm(perm.id)}
                                            title={t(`roleTemplates.actions.${perm.action}`, perm.action)}
                                          >
                                            {letter}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Save Bar */}
              <div className="sart-save-bar" style={{ display: moduleTab === 'VENTA' ? 'flex' : 'none' }}>
                {hasUnsaved && (
                  <div className="sart-unsaved">
                    <div className="sart-unsaved-dot" />
                    {t('roleTemplates.unsavedChanges')}
                  </div>
                )}
                <div className="sart-save-bar-right">
                  <button className="sart-btn sart-btn-ghost" onClick={() => setPendingPerms(null)}>
                    {t('roleTemplates.discard')}
                  </button>
                  <button
                    className="sart-btn sart-btn-warn"
                    onClick={() => setShowBroadcastConfirm(true)}
                    disabled={broadcastMutation.isPending}
                  >
                    <Radio style={{ width: 12, height: 12 }} />
                    {t('roleTemplates.broadcast')}
                  </button>
                  <button
                    className="sart-btn sart-btn-primary"
                    onClick={handleSave}
                    disabled={!hasUnsaved || updateMutation.isPending}
                  >
                    {updateMutation.isPending ? t('roleTemplates.saving') : t('roleTemplates.saveChanges')}
                  </button>
                </div>
              </div>
            </>
          ) : moduleTab === 'VENTA' ? (
            <div className="sart-empty">
              <div className="sart-empty-icon">
                <Shield style={{ width: 22, height: 22 }} />
              </div>
              <div className="sart-empty-title">{t('roleTemplates.selectTemplate')}</div>
            </div>
          ) : null}

          {/* ─── SUPERADMIN Tab ─── */}
          {moduleTab === 'SUPERADMIN' && (
            <div className="sart-perms-scroll">
              <div className="sart-sa-info-banner">
                <Info style={{ width: 14, height: 14, flexShrink: 0 }} />
                <span>{t('roleTemplates.saInfoBanner', 'SuperAdmin access is role-based. All users with the SUPER_ADMIN role have full platform access. Fine-grained permissions for SuperAdmin features are planned for a future release.')}</span>
              </div>
              {SUPERADMIN_SECTIONS.map(section => {
                const isOpen = expandedSaSections.has(section.key);
                const SectionIcon = section.icon;
                return (
                  <div key={section.key} className={`sart-section ${isOpen ? 'open' : ''}`}>
                    <div className="sart-section-hdr" onClick={() => toggleSaSection(section.key)}>
                      <div className="sart-section-icon" style={{ background: `color-mix(in srgb, ${section.colorVar} 15%, transparent)`, color: section.colorVar }}>
                        <SectionIcon style={{ width: 14, height: 14 }} />
                      </div>
                      <div className="sart-section-label">{t(section.labelKey)}</div>
                      <div className="sart-section-count" style={{ background: 'color-mix(in srgb, var(--color-text-muted) 15%, transparent)', color: 'var(--color-text-muted)' }}>
                        {section.items.length}
                      </div>
                      <ChevronDown className="sart-chev" style={{ width: 14, height: 14 }} />
                    </div>
                    {isOpen && (
                      <div className="sart-section-body">
                        {section.items.map(item => {
                          const ItemIcon = item.icon;
                          return (
                            <div key={item.key} className="sart-menu-item sart-sa-readonly">
                              <div className="sart-item-icon" style={{ color: section.colorVar }}>
                                <ItemIcon style={{ width: 13, height: 13 }} />
                              </div>
                              <div className="sart-item-label">{t(item.labelKey)}</div>
                              <div className="sart-sa-badge">{t('roleTemplates.saFullAccess', 'Full Access')}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── COMPTA Tab ─── */}
          {moduleTab === 'COMPTA' && (
            <div className="sart-empty">
              <div className="sart-empty-icon" style={{ background: 'color-mix(in srgb, var(--color-info) 12%, transparent)' }}>
                <BookOpenCheck style={{ width: 22, height: 22, color: 'var(--color-info)' }} />
              </div>
              <div className="sart-empty-title">{t('roleTemplates.comptaComingSoon', 'COMPTA Module')}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', maxWidth: 300, lineHeight: 1.5 }}>
                {t('roleTemplates.comptaComingSoonDesc', 'Accounting module permissions will appear here once the COMPTA module is available. Role templates will support per-module permission scoping.')}
              </div>
            </div>
          )}
        </div>

        {/* ═══ RIGHT PANEL ═══ */}
        <div className="sart-right-panel">
          <div className="sart-rp-head">
            <div className="sart-rp-title">
              <Radio style={{ width: 12, height: 12 }} />
              {t('roleTemplates.broadcastTargets')}
            </div>
            <div className="sart-rp-desc">{t('roleTemplates.broadcastTargetDesc')}</div>
          </div>

          <div className="sart-rp-scroll">
            <div style={{ marginBottom: 8 }}>
              {broadcastTargets.map(org => (
                <div key={org.id} className="sart-org-row">
                  <div className="sart-org-dot" />
                  <div className="sart-org-row-name">{org.name}</div>
                  <div className="sart-org-row-plan">{org.subscriptionTier}</div>
                </div>
              ))}
              {broadcastTargets.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', padding: '12px 10px' }}>
                  {t('roleTemplates.noTargetOrgs')}
                </div>
              )}
            </div>

            {/* Override note */}
            <div className="sart-override-note">
              <strong>&#9888; Note:</strong> {t('roleTemplates.overrideInfo')}
            </div>
          </div>

          {/* Audit Trail */}
          <div className="sart-rp-audit">
            <div className="sart-rp-audit-title">{t('roleTemplates.recentChanges')}</div>
            {selected?.updatedAt && (
              <div className="sart-audit-row">
                <div className="sart-audit-av">SA</div>
                <div className="sart-audit-body">
                  <div className="sart-audit-who">System</div>
                  <div className="sart-audit-what">
                    Updated <strong>{selected.name}</strong>
                  </div>
                  <div className="sart-audit-when">
                    {new Date(selected.updatedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}
                    {' — '}
                    {new Date(selected.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                    {' UTC'}
                  </div>
                </div>
              </div>
            )}
            <div className="sart-audit-row">
              <div className="sart-audit-av">SA</div>
              <div className="sart-audit-body">
                <div className="sart-audit-who">System</div>
                <div className="sart-audit-what">
                  Created <strong>{selected?.name || '—'}</strong> template
                </div>
                <div className="sart-audit-when">
                  {selected
                    ? `${new Date(selected.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })} — ${new Date(selected.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} UTC`
                    : '—'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ CREATE TEMPLATE MODAL ═══ */}
      {showNewModal && (
        <div className="sart-wizard-overlay open" onClick={() => setShowNewModal(false)}>
          <div className="sart-wizard" onClick={e => e.stopPropagation()}>
            <div className="sart-wiz-head">
              <div className="sart-wiz-title">{t('roleTemplates.createNew')}</div>
              <button className="sart-btn sart-btn-ghost" onClick={() => setShowNewModal(false)}>
                <X style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div className="sart-wiz-body">
              <label className="sart-field">
                <span>{t('roleTemplates.fields.code')}</span>
                <input
                  value={newTemplate.code}
                  onChange={e => setNewTemplate(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="FINANCE_MGR"
                  maxLength={50}
                />
              </label>
              <label className="sart-field">
                <span>{t('roleTemplates.fields.name')}</span>
                <input
                  value={newTemplate.name}
                  onChange={e => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Finance Manager Template"
                  maxLength={100}
                />
              </label>
              <label className="sart-field">
                <span>{t('roleTemplates.fields.description')}</span>
                <textarea
                  value={newTemplate.description}
                  onChange={e => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('roleTemplates.fields.descriptionPlaceholder')}
                  rows={3}
                  maxLength={500}
                />
              </label>
              <label className="sart-field">
                <span>{t('roleTemplates.fields.hierarchyLevel')}</span>
                <input
                  type="number"
                  value={newTemplate.hierarchyLevel}
                  onChange={e => setNewTemplate(prev => ({ ...prev, hierarchyLevel: parseInt(e.target.value) || 0 }))}
                  min={0}
                  max={100}
                />
              </label>
            </div>
            <div className="sart-wiz-foot">
              <button className="sart-btn sart-btn-ghost" onClick={() => setShowNewModal(false)}>
                {t('roleTemplates.cancel')}
              </button>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
                <button
                  className="sart-btn sart-btn-primary"
                  onClick={handleCreate}
                  disabled={!newTemplate.code || !newTemplate.name || createMutation.isPending}
                >
                  {createMutation.isPending && <Loader2 className="sart-spin" style={{ width: 14, height: 14 }} />}
                  {t('roleTemplates.create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ BROADCAST CONFIRMATION MODAL ═══ */}
      {showBroadcastConfirm && selected && (
        <div className="sart-wizard-overlay open" onClick={() => setShowBroadcastConfirm(false)}>
          <div className="sart-wizard" onClick={e => e.stopPropagation()}>
            <div className="sart-wiz-head">
              <div className="sart-wiz-title">{t('roleTemplates.confirmBroadcast')}</div>
              <button className="sart-btn sart-btn-ghost" onClick={() => setShowBroadcastConfirm(false)}>
                <X style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div className="sart-wiz-body">
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                {t('roleTemplates.broadcastConfirmText', {
                  name: selected.name,
                  count: broadcastTargets.length,
                })}
              </p>
              <div className="sart-override-note" style={{ marginTop: 12 }}>
                <strong>&#9888; Note:</strong> {t('roleTemplates.overrideInfo')}
              </div>
            </div>
            <div className="sart-wiz-foot">
              <button className="sart-btn sart-btn-ghost" onClick={() => setShowBroadcastConfirm(false)}>
                {t('roleTemplates.cancel')}
              </button>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
                <button
                  className="sart-btn sart-btn-warn"
                  onClick={handleBroadcast}
                  disabled={broadcastMutation.isPending}
                >
                  {broadcastMutation.isPending
                    ? <Loader2 className="sart-spin" style={{ width: 14, height: 14 }} />
                    : <Radio style={{ width: 12, height: 12 }} />
                  }
                  {t('roleTemplates.broadcastNow')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{pageStyles}</style>
    </>
  );
}

// ─── Role Card (left rail) ──────────────────────────────────────────────────

interface RoleCardProps {
  template: RoleTemplateDto;
  active: boolean;
  onClick: () => void;
}

function RoleCard({ template, active, onClick }: RoleCardProps) {
  return (
    <div className={`sart-role-card ${active ? 'active' : ''}`} onClick={onClick}>
      <div className={`sart-role-av ${active ? 'active' : ''}`}>
        {getTemplateInitials(template.code)}
      </div>
      <div className="sart-role-info">
        <div className="sart-role-name">{template.name}</div>
        <div className="sart-role-sub">
          {template.description || template.code}
        </div>
      </div>
      <div className={`sart-tag ${template.system ? 'sart-tag-plat' : 'sart-tag-tpl'}`}>
        {template.system ? 'SYSTEM' : 'GLOBAL'}
      </div>
    </div>
  );
}

// ─── Styles — Military-Precision Match to Access Grant v1.2 Mockup ──────────

const pageStyles = `
/* ═══════════════════════════════
   MAIN 3-COLUMN GRID
═══════════════════════════════ */
.sart-workspace {
  display: grid;
  grid-template-columns: 260px 1fr 300px;
  overflow: hidden;
  height: 100%;
}

/* ── LEFT RAIL ── */
.sart-left {
  background: var(--color-bg-secondary);
  border-right: 1px solid var(--color-border-subtle);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sart-rail-head {
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
}

.sart-rail-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.sart-rail-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.sart-search-wrap {
  position: relative;
}
.sart-search-wrap svg {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}
.sart-search-wrap input {
  width: 100%;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 7px;
  padding: 7px 10px 7px 29px;
  font-family: inherit;
  font-size: 12px;
  color: var(--color-text-primary);
  outline: none;
  transition: border-color .15s;
}
.sart-search-wrap input:focus { border-color: var(--color-brand-primary); }
.sart-search-wrap input::placeholder { color: var(--color-text-muted); }

.sart-role-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}
.sart-role-scroll::-webkit-scrollbar { width: 3px; }
.sart-role-scroll::-webkit-scrollbar-thumb { background: var(--color-border-default); border-radius: 2px; }

.sart-grp-label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: var(--color-text-muted);
  padding: 8px 6px 4px;
}

/* Role Card */
.sart-role-card {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  margin-bottom: 2px;
  transition: all .14s;
  position: relative;
}
.sart-role-card:hover { background: var(--color-bg-tertiary); }
.sart-role-card.active {
  background: color-mix(in srgb, var(--color-brand-primary) 12%, transparent);
  border-color: var(--color-brand-primary);
}
.sart-role-av {
  width: 30px; height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  font-family: var(--font-mono);
  flex-shrink: 0;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-subtle);
}
.sart-role-av.active {
  background: color-mix(in srgb, var(--color-brand-primary) 12%, transparent);
  color: var(--color-brand-primary);
  border-color: var(--color-brand-primary);
}

.sart-role-info { flex: 1; min-width: 0; }
.sart-role-name {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sart-role-sub {
  font-size: 10px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Tags */
.sart-tag {
  font-size: 9px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: .6px;
  flex-shrink: 0;
}
.sart-tag-plat {
  background: color-mix(in srgb, var(--color-text-muted) 8%, transparent);
  color: var(--color-text-muted);
}
.sart-tag-tpl {
  background: color-mix(in srgb, var(--color-success) 14%, transparent);
  color: var(--color-success);
}

/* New role button */
.sart-new-role-btn {
  margin: 8px 10px 12px;
  padding: 9px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: transparent;
  border: 1.5px dashed var(--color-border-default);
  border-radius: 8px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all .18s;
  flex-shrink: 0;
}
.sart-new-role-btn:hover {
  border-color: var(--color-brand-primary);
  color: var(--color-brand-primary);
  background: color-mix(in srgb, var(--color-brand-primary) 8%, transparent);
}

/* ── CENTER PANEL ── */
.sart-center {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-bg-primary);
}

/* Role Header */
.sart-role-hdr {
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-subtle);
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.sart-role-hdr-icon {
  width: 42px; height: 42px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  font-family: var(--font-mono);
  background: color-mix(in srgb, var(--color-brand-primary) 14%, transparent);
  color: var(--color-brand-primary);
  border: 1px solid var(--color-brand-primary);
  flex-shrink: 0;
}

.sart-role-hdr-info { flex: 1; min-width: 0; }
.sart-role-hdr-name {
  font-family: var(--font-heading);
  font-size: 20px;
  letter-spacing: 1.5px;
  color: var(--color-text-primary);
  line-height: 1;
}
.sart-role-hdr-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 3px;
}
.sart-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--color-text-muted);
}
.sart-meta-item svg { flex-shrink: 0; }
.sart-meta-item strong { color: var(--color-text-secondary); }
.sart-meta-dot {
  width: 3px; height: 3px;
  background: var(--color-border-default);
  border-radius: 50%;
}

/* Audit trail chip */
.sart-audit-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 20px;
  font-size: 10px;
  color: var(--color-text-secondary);
  font-weight: 500;
}
.sart-audit-chip svg { color: var(--color-text-muted); }

.sart-hdr-actions {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}

/* Buttons */
.sart-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 7px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  cursor: pointer;
  border: none;
  transition: all .15s;
  white-space: nowrap;
}
.sart-btn-ghost {
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
}
.sart-btn-ghost:hover { border-color: var(--color-brand-primary); color: var(--color-brand-primary); }
.sart-btn-primary { background: var(--color-brand-primary); color: #fff; }
.sart-btn-primary:hover { opacity: 0.9; }
.sart-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.sart-btn-warn {
  background: color-mix(in srgb, var(--color-warning) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-warning) 25%, transparent);
  color: var(--color-warning);
}
.sart-btn-warn:hover { border-color: var(--color-warning); }
.sart-btn-warn:disabled { opacity: 0.5; cursor: not-allowed; }
.sart-btn-sm { padding: 5px 10px; font-size: 10px; }

/* Toolbar */
.sart-toolbar {
  padding: 10px 24px;
  background: var(--color-bg-primary);
  border-bottom: 1px solid var(--color-border-subtle);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.sart-tb-search {
  position: relative;
  width: 200px;
}
.sart-tb-search svg {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}
.sart-tb-search input {
  width: 100%;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 6px;
  padding: 6px 9px 6px 27px;
  font-family: inherit;
  font-size: 11px;
  color: var(--color-text-primary);
  outline: none;
}
.sart-tb-search input:focus { border-color: var(--color-brand-primary); }
.sart-tb-search input::placeholder { color: var(--color-text-muted); }

.sart-tb-sep { width: 1px; height: 18px; background: var(--color-border-subtle); }

.sart-tb-btn {
  padding: 5px 11px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 6px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .5px;
  text-transform: uppercase;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all .14s;
}
.sart-tb-btn:hover { border-color: var(--color-brand-primary); color: var(--color-brand-primary); }

.sart-tb-right { margin-left: auto; display: flex; align-items: center; gap: 8px; }

.sart-counter {
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 4px 10px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 6px;
  color: var(--color-text-muted);
}
.sart-counter b { color: var(--color-brand-primary); }

/* Perms scroll area */
.sart-perms-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sart-perms-scroll::-webkit-scrollbar { width: 4px; }
.sart-perms-scroll::-webkit-scrollbar-thumb { background: var(--color-border-default); border-radius: 2px; }

/* Section Accordion */
.sart-section {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.sart-section.open {
  box-shadow: var(--shadow-sm);
}
.sart-section-hdr {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
}
.sart-section-hdr:hover {
  background: var(--color-bg-tertiary);
}
.sart-section-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  flex-shrink: 0;
}
.sart-section-label {
  font-weight: 600;
  font-size: 13px;
  flex: 1;
  color: var(--color-text-primary);
}
.sart-section-toggle {
  width: 20px;
  height: 20px;
  border-radius: var(--radius-sm);
  border: 2px solid var(--color-border-default);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.sart-section-toggle.on {
  background: var(--color-success);
  border-color: var(--color-success);
  color: #fff;
}
.sart-section-toggle.partial {
  background: color-mix(in srgb, var(--color-success) 40%, transparent);
  border-color: var(--color-success);
}
.sart-section-count {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--color-error) 15%, transparent);
  color: var(--color-error);
}
.sart-chev {
  color: var(--color-text-muted);
  transition: transform 0.2s;
  flex-shrink: 0;
}
.sart-section.open .sart-chev {
  transform: rotate(180deg);
}

/* Menu Items inside section */
.sart-section-body {
  border-top: 1px solid var(--color-border-subtle);
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sart-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  transition: background 0.12s;
}
.sart-menu-item:hover {
  background: var(--color-bg-tertiary);
}

/* Toggle switch (rounded pill) */
.sart-item-toggle {
  width: 32px;
  height: 18px;
  border-radius: 9px;
  background: var(--color-border-default);
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
.sart-item-toggle::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}
.sart-item-toggle.on {
  background: var(--color-error);
}
.sart-item-toggle.on::after {
  transform: translateX(14px);
}
.sart-item-toggle.partial {
  background: color-mix(in srgb, var(--color-error) 60%, var(--color-border-default));
}
.sart-item-toggle.partial::after {
  transform: translateX(7px);
}

.sart-item-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.7;
}
.sart-item-label {
  flex: 1;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--color-text-primary);
}

/* Action pills */
.sart-item-pills {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.sart-pill {
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  background: var(--color-bg-tertiary);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-subtle);
}
.sart-pill.on {
  background: var(--color-error);
  color: #fff;
  border-color: var(--color-error);
}
.sart-pill:hover {
  transform: scale(1.08);
}

/* Save footer */
.sart-save-bar {
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border-subtle);
  padding: 11px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.sart-unsaved {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 9px;
  background: color-mix(in srgb, var(--color-warning) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-warning) 25%, transparent);
  border-radius: 12px;
  color: var(--color-warning);
  letter-spacing: .5px;
}
.sart-unsaved-dot {
  width: 5px; height: 5px;
  background: var(--color-warning);
  border-radius: 50%;
  animation: sart-blink .9s ease-in-out infinite;
}
.sart-save-bar-right { margin-left: auto; display: flex; gap: 7px; }

/* ── RIGHT PANEL ── */
.sart-right-panel {
  background: var(--color-bg-secondary);
  border-left: 1px solid var(--color-border-subtle);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sart-rp-head {
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
}
.sart-rp-title {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sart-rp-desc {
  font-size: 11px;
  color: var(--color-text-muted);
  line-height: 1.5;
}

.sart-rp-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}
.sart-rp-scroll::-webkit-scrollbar { width: 3px; }
.sart-rp-scroll::-webkit-scrollbar-thumb { background: var(--color-border-default); border-radius: 2px; }

/* Org row */
.sart-org-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--color-bg-secondary);
  margin-bottom: 5px;
  border: 1px solid var(--color-border-subtle);
}
.sart-org-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--color-success);
  flex-shrink: 0;
}
.sart-org-row-name {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
}
.sart-org-row-plan {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--color-bg-tertiary);
  color: var(--color-text-muted);
}

/* Override note */
.sart-override-note {
  padding: 8px 10px;
  background: color-mix(in srgb, var(--color-warning) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-warning) 20%, transparent);
  border-radius: 7px;
  font-size: 10px;
  color: var(--color-warning);
  line-height: 1.5;
}

/* Audit trail in right panel */
.sart-rp-audit {
  border-top: 1px solid var(--color-border-subtle);
  padding: 12px;
  flex-shrink: 0;
}
.sart-rp-audit-title {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 8px;
}
.sart-audit-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 7px;
  font-size: 11px;
}
.sart-audit-av {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--color-brand-primary) 14%, transparent);
  border: 1px solid var(--color-brand-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 800;
  color: var(--color-brand-primary);
  flex-shrink: 0;
  font-family: var(--font-mono);
}
.sart-audit-body { flex: 1; min-width: 0; }
.sart-audit-who { font-weight: 700; color: var(--color-text-primary); font-size: 11px; }
.sart-audit-what { font-size: 10px; color: var(--color-text-muted); margin-top: 1px; }
.sart-audit-when {
  font-size: 9px;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  margin-top: 1px;
}

/* Empty state */
.sart-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 8px;
  color: var(--color-text-muted);
  text-align: center;
  padding: 20px;
}
.sart-empty-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  background: var(--color-bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
}
.sart-empty-title { font-size: 13px; font-weight: 700; color: var(--color-text-secondary); }

/* ═══ WIZARD / MODAL OVERLAY ═══ */
.sart-wizard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.75);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity .2s;
}
.sart-wizard-overlay.open {
  opacity: 1;
  pointer-events: all;
}

.sart-wizard {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  width: 480px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(12px) scale(.98);
  transition: transform .25s ease;
  box-shadow: var(--shadow-xl);
}
.sart-wizard-overlay.open .sart-wizard {
  transform: translateY(0) scale(1);
}

.sart-wiz-head {
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sart-wiz-title {
  font-family: var(--font-heading);
  font-size: 22px;
  letter-spacing: 2px;
  color: var(--color-brand-primary);
  line-height: 1;
}

.sart-wiz-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}
.sart-wiz-body::-webkit-scrollbar { width: 3px; }
.sart-wiz-body::-webkit-scrollbar-thumb { background: var(--color-border-default); }

.sart-wiz-foot {
  padding: 14px 24px;
  border-top: 1px solid var(--color-border-subtle);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* Fields */
.sart-field {
  display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px;
}
.sart-field span {
  font-size: 11px; font-weight: 600; color: var(--color-text-secondary);
}
.sart-field input, .sart-field textarea {
  padding: 8px 10px; border-radius: 7px;
  border: 1px solid var(--color-border-default); background: var(--input-bg);
  font-size: 12px; color: var(--color-text-primary); outline: none;
  transition: border-color .15s;
  font-family: inherit;
}
.sart-field input:focus, .sart-field textarea:focus {
  border-color: var(--color-brand-primary); box-shadow: var(--input-focus-ring);
}
.sart-field textarea { resize: vertical; font-family: var(--font-body); }

/* Animations */
@keyframes sart-blink { 0%,100%{opacity:1} 50%{opacity:.3} }
@keyframes sart-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.sart-spin { animation: sart-spin 1s linear infinite; }

/* ── MODULE TABS ── */
.sart-module-tabs {
  display: flex;
  gap: 2px;
  padding: 8px 24px 0;
  background: var(--color-bg-primary);
  border-bottom: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
}
.sart-module-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  color: var(--color-text-muted);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all .15s;
  margin-bottom: -1px;
}
.sart-module-tab:hover {
  color: var(--tab-color, var(--color-text-secondary));
}
.sart-module-tab.active {
  color: var(--tab-color, var(--color-brand-primary));
  border-bottom-color: var(--tab-color, var(--color-brand-primary));
}

/* ── SUPERADMIN TAB ── */
.sart-sa-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  background: color-mix(in srgb, var(--color-info) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-info) 20%, transparent);
  border-radius: var(--radius-lg);
  margin-bottom: 12px;
  color: var(--color-info);
  font-size: 11px;
  line-height: 1.6;
}
.sart-sa-readonly {
  opacity: 0.85;
}
.sart-sa-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--color-success) 12%, transparent);
  color: var(--color-success);
  letter-spacing: .4px;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .sart-workspace { grid-template-columns: 220px 1fr 260px; }
}
@media (max-width: 768px) {
  .sart-workspace { grid-template-columns: 1fr; grid-template-rows: auto 1fr auto; }
  .sart-left { max-height: 250px; }
  .sart-right-panel { max-height: 200px; }
}
@media (prefers-reduced-motion: reduce) {
  .sart-unsaved-dot, .sart-wizard-overlay, .sart-wizard { animation: none !important; transition: none !important; }
}
`;
